import React from 'react';
import { Users, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { getTransactionStatus, getTransactionType } from '../../utils/helpers';

const Transactions = ({
    transactions,
    showAllTransactions,
    toggleTransactionView,
    pagination,
    currentPage,
    handlePageChange,
    sessionId,
    isMobile
}) => {
    const { theme } = useTheme();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '24px' : '34px' }}>
            <div style={{ textAlign: 'center', animation: 'bubbleAppear 1s ease-in-out' }}>
                <h2 style={{
                    color: theme.gold,
                    fontWeight: 'bolder',
                    textTransform: 'uppercase',
                    fontSize: isMobile ? '35px' : '51px',
                    margin: '0 0 10px 0'
                }}>
                    Transaction History
                </h2>
                <p style={{
                    color: theme.accent,
                    fontWeight: 'bold',
                    fontSize: isMobile ? '20px' : '30px',
                    margin: '0'
                }}>
                    Track auction bids and inclusion status
                </p>
            </div>

            <div style={{
                backgroundColor: theme.panelBg,
                borderRadius: '10px',
                boxShadow: '0 4px 7px rgba(40, 40, 40, 1)',
                animation: 'bubbleAppear 0.5s ease-in-out'
            }}>
                <div style={{
                    padding: isMobile ? '17px' : '26px',
                    borderBottom: `3px solid ${theme.accent}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '12px'
                }}>
                    <h3 style={{
                        color: theme.gold,
                        fontWeight: 'bolder',
                        textTransform: 'uppercase',
                        margin: '0',
                        fontSize: isMobile ? '24px' : '32px'
                    }}>
                        {showAllTransactions ? 'All Transactions' : 'My Transactions'}
                    </h3>

                    <button
                        onClick={toggleTransactionView}
                        style={{
                            backgroundColor: showAllTransactions ? '#16a085' : '#16a085',
                            color: theme.text,
                            padding: isMobile ? '8px 12px' : '10px 16px',
                            border: 'none',
                            borderRadius: '5px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            cursor: 'pointer',
                            fontFamily: 'monospace',
                            boxShadow: '0 2px 4px rgba(40, 40, 40, 0.7)',
                            fontSize: isMobile ? '12px' : '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'transform 0.3s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                        onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                    >
                        {showAllTransactions ? <Users size={16} /> : <User size={16} />}
                        {showAllTransactions ? 'Show Mine' : 'Show All'}
                    </button>
                </div>

                <div style={{ padding: isMobile ? '17px' : '26px' }}>
                    {transactions.length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            color: theme.accent,
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            padding: isMobile ? '24px' : '34px',
                            fontSize: isMobile ? '20px' : '23px'
                        }}>
                            {showAllTransactions ? 'No transactions in the system yet!' : 'No transactions yet. Submit some bids!'}
                        </div>
                    ) : (
                        <>
                            {transactions.map(tx => (
                                <div key={tx.id} style={{
                                    backgroundColor: theme.cardBg,
                                    borderRadius: '5px',
                                    boxShadow: '0 4px 7px rgba(40, 40, 40, 1)',
                                    padding: isMobile ? '17px' : '20px',
                                    marginBottom: isMobile ? '12px' : '17px'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: '12px',
                                        flexWrap: 'wrap',
                                        gap: '8px'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{
                                                color: '#75bd4f',
                                                fontWeight: 'bold',
                                                fontSize: isMobile ? '16px' : '18px'
                                            }}>
                                                {tx.id.slice(0, 8)}...
                                            </span>
                                            {showAllTransactions && (
                                                <span style={{
                                                    fontSize: isMobile ? '12px' : '14px',
                                                    color: theme.text,
                                                    backgroundColor: tx.sender === sessionId ? theme.gold : theme.textDim,
                                                    padding: '2px 6px',
                                                    borderRadius: '3px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    {tx.sender === sessionId ? 'YOU' : tx.sender.slice(0, 6) + '...'}
                                                </span>
                                            )}
                                        </div>
                                        <span style={{
                                            padding: '4px 12px',
                                            borderRadius: '3px',
                                            fontSize: isMobile ? '12px' : '14px',
                                            fontWeight: 'bold',
                                            textTransform: 'uppercase',
                                            backgroundColor:
                                                getTransactionStatus(tx.status) === 'Included' ? '#75bd4f' :
                                                    getTransactionStatus(tx.status) === 'Auction Won' ? theme.gold :
                                                        getTransactionStatus(tx.status) === 'Failed' ? '#dc2626' : '#666',
                                            color: theme.text
                                        }}>
                                            {getTransactionStatus(tx.status)}
                                        </span>
                                    </div>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                                        gap: '8px',
                                        fontSize: isMobile ? '14px' : '16px',
                                        color: theme.text
                                    }}>
                                        <div>
                                            <strong>Type:</strong> {getTransactionType(tx.inclusion_type)}
                                        </div>
                                        <div>
                                            <strong>Fee:</strong> {tx.priority_fee} SOL
                                        </div>
                                        <div>
                                            <strong>Compute Units:</strong> {tx.compute_units}
                                        </div>
                                        <div>
                                            <strong>Created:</strong> {new Date(tx.created_at).toLocaleTimeString()}
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {pagination.total_pages > 1 && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '12px',
                                    marginTop: '20px',
                                    flexWrap: 'wrap'
                                }}>
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={!pagination.has_prev}
                                        style={{
                                            backgroundColor: pagination.has_prev ? '#16a085' : '#666',
                                            color: theme.text,
                                            border: 'none',
                                            borderRadius: '5px',
                                            padding: '8px 12px',
                                            cursor: pagination.has_prev ? 'pointer' : 'not-allowed',
                                            fontFamily: 'monospace',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                    >
                                        <ChevronLeft size={16} />
                                        Prev
                                    </button>

                                    <div style={{
                                        color: theme.accent,
                                        fontWeight: 'bold',
                                        fontSize: isMobile ? '14px' : '16px',
                                        padding: '0 12px'
                                    }}>
                                        {currentPage} of {pagination.total_pages}
                                    </div>

                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={!pagination.has_next}
                                        style={{
                                            backgroundColor: pagination.has_next ? '#16a085' : '#666',
                                            color: theme.text,
                                            border: 'none',
                                            borderRadius: '5px',
                                            padding: '8px 12px',
                                            cursor: pagination.has_next ? 'pointer' : 'not-allowed',
                                            fontFamily: 'monospace',
                                            fontWeight: 'bold',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}
                                    >
                                        Next
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            )}

                            <div style={{
                                textAlign: 'center',
                                marginTop: '12px',
                                color: theme.accent,
                                fontSize: isMobile ? '12px' : '14px',
                                fontWeight: 'bold'
                            }}>
                                Showing {transactions.length} of {pagination.total_count} transactions
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Transactions;
